import { Cookies } from "react-cookie";

import { ResponseType } from "../types/api";
export class ApiError extends Error {
  response: {
    data: any;
    status: number;
    statusText: string;
    headers: Headers;
  } | null;
  config: RequestInit;

  constructor(
    message: string,
    response: {
      data: any;
      status: number;
      statusText: string;
      headers: Headers;
    } | null = null,
    config: RequestInit = {}
  ) {
    super(message);
    this.name = "ApiError";
    this.response = response;
    this.config = config;
  }
}

let platformUrl: string | null = null;

export function setPlatformUrl(url: string | null): void {
  platformUrl = url;
}

export function getPlatformUrl(): string {
  return platformUrl || process.env.REACT_APP_XCAUSAL_PLATFORM_URL || "";
}

function getToken(): string {
  const token = new Cookies().get("token");
  if (!token) {
    throw new ApiError(
      "Unauthorized: Token is missing",
      {
        status: 401,
        data: { message: "Token is missing" },
        statusText: "Unauthorized",
        headers: new Headers(),
      },
      {}
    );
  }
  return token;
}

interface BaseOptions {
  responseType?: ResponseType;
  useToken?: boolean;
  token?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

interface RequestParams extends BaseOptions {
  url: string;
  body?: unknown;
}

async function request<T>(
  method: string,
  url: string,
  body: unknown,
  {
    responseType = "json",
    useToken = true,
    token,
    headers = {},
    signal,
  }: BaseOptions = {}
): Promise<T> {
  const finalHeaders: Record<string, string> = { Vary: "Origin", ...headers };

  if (responseType === "json" && body != null && !(body instanceof FormData)) {
    finalHeaders["Content-Type"] = "application/json";
  }

  if (useToken) {
    const authToken = token || getToken();
    finalHeaders["Authorization"] = `Bearer ${authToken}`;
  }

  let fetchBody: BodyInit | undefined;
  if (body instanceof FormData) {
    fetchBody = body;
  } else if (body != null) {
    fetchBody = JSON.stringify(body);
  } else {
    fetchBody = undefined;
  }

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    body: fetchBody,
    signal,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiError(
      response.statusText,
      {
        status: response.status,
        data,
        statusText: response.statusText,
        headers: response.headers,
      },
      { method, headers: finalHeaders, body: fetchBody }
    );
  }

  switch (responseType) {
    case "blob":
      return (await response.blob()) as unknown as T;
    case "arrayBuffer":
      return (await response.arrayBuffer()) as unknown as T;
    case "text":
      return (await response.text()) as unknown as T;
    default:
      return response.json();
  }
}

export const apiClient = {
  get: <T = any>({
    url,
    responseType,
    useToken,
    token,
    headers,
    signal,
  }: Omit<RequestParams, "body">): Promise<T> =>
    request<T>("GET", url, undefined, {
      responseType,
      useToken,
      token,
      headers,
      signal,
    }),

  post: <T = any>({
    url,
    body,
    responseType,
    useToken,
    token,
    headers,
    signal,
  }: RequestParams): Promise<T> =>
    request<T>("POST", url, body, {
      responseType,
      useToken,
      token,
      headers,
      signal,
    }),

  update: <T = any>({
    url,
    body,
    responseType,
    useToken,
    token,
    headers,
    signal,
  }: RequestParams): Promise<T> =>
    request<T>("PUT", url, body, {
      responseType,
      useToken,
      token,
      headers,
      signal,
    }),

  patch: <T = any>({
    url,
    body,
    responseType,
    useToken,
    token,
    headers,
    signal,
  }: RequestParams): Promise<T> =>
    request<T>("PATCH", url, body, {
      responseType,
      useToken,
      token,
      headers,
      signal,
    }),

  remove: <T = any>({
    url,
    body,
    responseType,
    useToken,
    token,
    headers,
    signal,
  }: RequestParams): Promise<T> =>
    request<T>("DELETE", url, body, {
      responseType,
      useToken,
      token,
      headers,
      signal,
    }),

  upload: <T = any>({
    url,
    body: form,
    responseType,
    useToken,
    token,
    headers,
    signal,
  }: RequestParams & { body: FormData }): Promise<T> =>
    request<T>("POST", url, form, {
      responseType,
      useToken,
      token,
      headers,
      signal,
    }),
};

// message-queue（非同期タスク）: task_id 個別操作（UI側でポーリング制御する用）。
// status は { id, status } を返す（status truthy で完了）。
export async function getTaskStatus(taskId: string): Promise<{ status?: boolean }> {
  return apiClient.get({
    url: `${getPlatformUrl()}/api/message-queue/status?task_id=${taskId}`,
    useToken: true,
  });
}

// result は { id, task_result } を返す。task_result をアンラップして返す。
export async function getTaskResult<T = any>(taskId: string): Promise<T> {
  const res: any = await apiClient.get({
    url: `${getPlatformUrl()}/api/message-queue/result?task_id=${taskId}`,
    useToken: true,
  });
  return (res?.task_result ?? res) as T;
}

// message-queue（非同期タスク）共通ポーリング。
// POST 実行で得た task_id を渡すと、status が返るまで一定間隔でポーリングし、
// 完了後に result を取得して返す。参考: graph legacy の pollMessageQueue。
export async function pollTask<T = any>(
  taskId: string,
  intervalMs = 10000
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const intervalId = setInterval(async () => {
      try {
        const statusResult: any = await apiClient.get({
          url: `${getPlatformUrl()}/api/message-queue/status?task_id=${taskId}`,
          useToken: true,
        });
        if (statusResult !== undefined && statusResult.status) {
          clearInterval(intervalId);
          const finalResult: any = await apiClient.get({
            url: `${getPlatformUrl()}/api/message-queue/result?task_id=${taskId}`,
            useToken: true,
          });
          // mq の result は { id, task_result: {...} } でラップされる。
          // 実データ（task_result）を返し、旧同期APIと同じ形にする。
          resolve((finalResult?.task_result ?? finalResult) as T);
        }
      } catch (error) {
        clearInterval(intervalId);
        reject(error);
      }
    }, intervalMs);
  });
}
